import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { BRAND } from "@/constants/brand";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      const target = location.state?.from?.pathname || (u.onboarded ? "/app" : "/onboarding");
      navigate(target, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return <AuthShell heading="Welcome back." sub="Sign in to your workspace.">
    <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
      <Field label="Email" type="email" value={email} onChange={setEmail} testId="login-email" />
      <Field label="Password" type="password" value={password} onChange={setPassword} testId="login-password" />
      <button type="submit" disabled={loading}
        className="w-full bg-[#D4AF37] text-[#0A0A0A] py-3 text-sm font-medium hover:bg-[#F59E0B] disabled:opacity-50 transition-colors"
        data-testid="login-submit">
        {loading ? "Signing in…" : "Sign in"}
      </button>
    </form>
    <div className="mt-6 text-sm text-[#A1A1AA] flex justify-between">
      <Link to="/forgot-password" className="hover:text-[#FFFFF0]">Forgot password?</Link>
      <Link to="/signup" className="text-[#D4AF37]" data-testid="link-signup">Create account</Link>
    </div>
  </AuthShell>;
}

export function Signup() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signup(name, email, password);
      toast.success("Welcome to StoryFrame AI");
      navigate("/onboarding", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  return <AuthShell heading="Create your workspace." sub="Free forever. No credit card.">
    <form onSubmit={onSubmit} className="space-y-4" data-testid="signup-form">
      <Field label="Full name" value={name} onChange={setName} testId="signup-name" />
      <Field label="Email" type="email" value={email} onChange={setEmail} testId="signup-email" />
      <Field label="Password" type="password" value={password} onChange={setPassword} testId="signup-password" hint="Min 6 characters" />
      <button type="submit" disabled={loading}
        className="w-full bg-[#D4AF37] text-[#0A0A0A] py-3 text-sm font-medium hover:bg-[#F59E0B] disabled:opacity-50 transition-colors"
        data-testid="signup-submit">
        {loading ? "Creating…" : "Create account"}
      </button>
    </form>
    <div className="mt-6 text-sm text-[#A1A1AA] text-center">
      Already a user? <Link to="/login" className="text-[#D4AF37]" data-testid="link-login">Sign in</Link>
    </div>
  </AuthShell>;
}

function AuthShell({ heading, sub, children }) {
  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#0A0A0A]">
      <div className="hidden lg:block relative sf-noise">
        <div className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{ backgroundImage: "url('https://images.pexels.com/photos/10395639/pexels-photo-10395639.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')" }}></div>
        <div className="absolute inset-0 bg-[#0A0A0A]/70 sf-vignette"></div>
        <div className="relative h-full flex flex-col justify-between p-12">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-7 h-7 border border-[#D4AF37] flex items-center justify-center">
              <div className="w-2 h-2 bg-[#D4AF37]"></div>
            </div>
            <span className="font-editorial text-xl">{BRAND.short}<span className="text-[#D4AF37]"> AI</span></span>
          </Link>
          <div>
            <div className="font-editorial text-4xl leading-tight max-w-md">
              &ldquo;The story is<br /><span className="italic text-[#D4AF37]">the source of truth.</span>&rdquo;
            </div>
            <div className="mt-4 text-sm text-[#A1A1AA]">&mdash; the working principle of every project inside StoryFrame.</div>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-8 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8">
            <Link to="/" className="font-editorial text-xl">{BRAND.short} <span className="text-[#D4AF37]">AI</span></Link>
          </div>
          <h1 className="font-editorial text-4xl mb-2">{heading}</h1>
          <p className="text-sm text-[#A1A1AA] mb-8">{sub}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, testId, hint }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-widest text-[#71717A] mb-2">{label}</div>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
        className="w-full bg-[#141414] border border-[#27272A] focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] text-[#FFFFF0] px-4 py-3 text-sm placeholder-[#71717A]"
        data-testid={testId}
      />
      {hint && <div className="text-[10px] text-[#71717A] mt-1.5">{hint}</div>}
    </label>
  );
}
